import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { PostCard } from "@/components/posts/PostCard";
import {
    ArrowRight,
    ShieldCheck,
    Users,
    Store,
    ClipboardList,
    Sparkles,
    Plus,
} from "lucide-react";

const KpiTile = ({ label, value, icon: Icon, to, testId }) => {
    const inner = (
        <div className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl nest-gradient text-white nest-shadow">
                <Icon size={22} />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                    {label}
                </div>
                <div className="mt-0.5 text-3xl font-extrabold text-neutral-900" data-testid={testId}>
                    {value ?? "—"}
                </div>
            </div>
        </div>
    );
    return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function DashboardPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [posts, setPosts] = useState([]); // public feed sample
    const [myPosts, setMyPosts] = useState([]);
    const [users, setUsers] = useState([]);
    const [pendingShops, setPendingShops] = useState([]);
    const [adminPosts, setAdminPosts] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const calls = [api.get("/posts?limit=8")];
            calls.push(api.get("/me/posts?limit=8").catch(() => ({ data: [] })));
            if (["SUPER_ADMIN"].includes(user?.role)) {
                calls.push(api.get("/admin/users").catch(() => ({ data: [] })));
                calls.push(
                    api
                        .get("/admin/users?role=SHOP_OWNER&status=PENDING_APPROVAL")
                        .catch(() => ({ data: [] })),
                );
            }
            if (
                ["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR"].includes(
                    user?.role,
                )
            ) {
                calls.push(api.get("/admin/posts?limit=200").catch(() => ({ data: [] })));
            }
            const results = await Promise.all(calls);
            setPosts(results[0].data);
            setMyPosts(results[1].data || []);
            let idx = 2;
            if (["SUPER_ADMIN"].includes(user?.role)) {
                setUsers(results[idx++].data || []);
                setPendingShops(results[idx++].data || []);
            }
            if (
                ["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR"].includes(
                    user?.role,
                )
            ) {
                setAdminPosts(results[idx++].data || []);
            }
            setLoading(false);
        })();
    }, [user?.role]);

    const totalPublished = useMemo(
        () => adminPosts.filter((p) => p.status === "PUBLISHED").length || posts.length,
        [adminPosts, posts],
    );
    const totalDrafts = useMemo(
        () => adminPosts.filter((p) => p.status === "DRAFT").length,
        [adminPosts],
    );
    const activeShops = useMemo(
        () => users.filter((u) => u.role === "SHOP_OWNER" && u.status === "ACTIVE").length,
        [users],
    );

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">
                        {new Date().toLocaleDateString(undefined, {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                        })}
                    </div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                        Namaskaram,{" "}
                        <span className="nest-gradient-text">
                            {(user?.full_name || "").split(" ")[0]}
                        </span>
                        .
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Here's what's happening across your OfferNest today.
                    </p>
                </div>
                {["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR", "STAFF", "SHOP_OWNER"].includes(
                    user?.role,
                ) && (
                    <Link to="/posts/new">
                        <Button className="btn-primary" data-testid="dash-create-post-btn">
                            <Plus size={16} className="mr-2" />
                            {user?.role === "SHOP_OWNER" ? "Create offer" : "Create post"}
                        </Button>
                    </Link>
                )}
            </header>

            {/* KPIs */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 rounded-2xl" />
                    ))
                ) : (
                    <>
                        <KpiTile
                            label={user?.role === "SHOP_OWNER" ? "My live offers" : "Live posts"}
                            value={
                                user?.role === "SHOP_OWNER"
                                    ? myPosts.filter((p) => p.status === "PUBLISHED").length
                                    : totalPublished
                            }
                            icon={ClipboardList}
                            to={
                                ["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER"].includes(user?.role)
                                    ? "/admin/posts"
                                    : "/me/posts"
                            }
                            testId="kpi-live"
                        />
                        {["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR"].includes(
                            user?.role,
                        ) ? (
                            <KpiTile
                                label="Drafts awaiting review"
                                value={totalDrafts}
                                icon={Sparkles}
                                to="/admin/posts?status=DRAFT"
                                testId="kpi-drafts"
                            />
                        ) : (
                            <KpiTile
                                label="My drafts"
                                value={myPosts.filter((p) => p.status === "DRAFT").length}
                                icon={Sparkles}
                                to="/me/posts"
                                testId="kpi-my-drafts"
                            />
                        )}
                        {user?.role === "SUPER_ADMIN" ? (
                            <>
                                <KpiTile
                                    label="Pending shops"
                                    value={pendingShops.length}
                                    icon={ShieldCheck}
                                    to="/admin/shops/pending"
                                    testId="kpi-pending-shops"
                                />
                                <KpiTile
                                    label="Active shops"
                                    value={activeShops}
                                    icon={Store}
                                    to="/admin/users?role=SHOP_OWNER"
                                    testId="kpi-active-shops"
                                />
                            </>
                        ) : (
                            <>
                                <KpiTile
                                    label="Public feed items"
                                    value={posts.length}
                                    icon={Store}
                                    to="/posts"
                                    testId="kpi-feed"
                                />
                                <KpiTile
                                    label="My posts total"
                                    value={myPosts.length}
                                    icon={Users}
                                    to="/me/posts"
                                    testId="kpi-my-total"
                                />
                            </>
                        )}
                    </>
                )}
            </section>

            {/* Latest published feed */}
            <section>
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-neutral-900">Latest from the feed</h2>
                    <Link to="/posts" className="text-sm font-medium text-orange-600" data-testid="dash-view-all-feed">
                        See all <ArrowRight size={14} className="inline" />
                    </Link>
                </div>
                {loading ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-64 rounded-2xl" />
                        ))}
                    </div>
                ) : posts.length ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {posts.slice(0, 8).map((p) => (
                            <PostCard key={p.id} post={p} />
                        ))}
                    </div>
                ) : (
                    <EmptyState
                        title="No published posts yet"
                        hint="Once shop owners and staff start publishing, they'll show up here."
                    />
                )}
            </section>
        </div>
    );
}
